import { Reveal } from "./Reveal";
import { MapPin } from "lucide-react";

const SUMMARY = [
  ["20+", "Cities covered"],
  ["2.4M", "Samples handed out"],
  ["500+", "Activations run"],
];

const CASES = [
  {
    metric: "240K",
    metricLabel: "Samples handed out",
    brand: "Summit Energy",
    campaign: "Citywide sampling blitz",
    city: "Los Angeles",
    story:
      "42 geo-verified activations across 18 neighborhoods in six weeks. Every sample scanned, every setup photographed — clients watched coverage fill in live on the dashboard.",
    stats: [
      ["42", "Activations"],
      ["18", "Neighborhoods"],
    ],
  },
  {
    metric: "1.2M",
    metricLabel: "Street impressions",
    brand: "Nova Sneakers",
    campaign: "Launch-day takeover",
    city: "New York City",
    story:
      "60 ambassadors deployed across 3 boroughs for a single drop. Shift assignments, routes, and briefs pushed in-app the night before — zero no-shows, full photo proof by noon.",
    stats: [
      ["60", "Ambassadors"],
      ["3", "Boroughs"],
    ],
  },
  {
    metric: "96%",
    metricLabel: "Check-in accuracy",
    brand: "Rivera Coffee",
    campaign: "Morning-rush pop-ups",
    city: "LA · Remote",
    story:
      "A 12-week pop-up run across 8 markets. Geofenced check-ins killed buddy-punching and gave the ops team clean, payroll-ready hours the moment each shift ended.",
    stats: [
      ["12wk", "Program"],
      ["8", "Markets"],
    ],
  },
  {
    metric: "18",
    metricLabel: "Cities on tour",
    brand: "Pulse Audio",
    campaign: "Festival circuit",
    city: "National · Remote",
    story:
      "Offline-first field ops through dead zones and festival grounds. 5,400 leads captured and synced automatically once signal returned — nothing lost in the noise.",
    stats: [
      ["5.4K", "Leads captured"],
      ["220", "Shifts"],
    ],
  },
];

export const CaseStudies = () => {
  return (
    <section id="work" data-testid="casestudies-section" className="bg-paper text-ink">
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-32">
        {/* header */}
        <Reveal className="mb-12 flex flex-col gap-8 border-b-2 border-ink pb-10 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-orange">/ Case studies</p>
            <h2 className="font-bebas text-5xl uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl">
              Proof from <br /> the pavement.
            </h2>
          </div>
          <div className="flex gap-8 sm:gap-12">
            {SUMMARY.map(([n, l]) => (
              <div key={l}>
                <p className="font-bebas text-4xl leading-none text-ink sm:text-5xl">{n}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted2">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* editorial rows */}
        <div className="border-t-2 border-ink">
          {CASES.map((c, i) => (
            <Reveal
              key={c.brand}
              delay={i * 0.06}
              className="group grid grid-cols-1 items-start gap-6 border-b-2 border-ink py-10 md:grid-cols-12 md:gap-8"
            >
              {/* big metric */}
              <div className="md:col-span-3">
                <p className="font-bebas text-7xl leading-[0.85] tracking-tight text-orange transition-transform duration-300 group-hover:-translate-y-1 sm:text-8xl">
                  {c.metric}
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted2">
                  {c.metricLabel}
                </p>
              </div>

              {/* title + city */}
              <div className="md:col-span-4">
                <h3 className="font-bebas text-4xl uppercase leading-none tracking-tight">{c.brand}</h3>
                <p className="mt-1 font-mono text-sm text-ink/80">{c.campaign}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 border-2 border-ink bg-ink px-2.5 py-1">
                  <MapPin size={12} className="text-orange" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-paper">{c.city}</span>
                </span>
              </div>

              {/* story + mini stats */}
              <div className="md:col-span-5">
                <p className="font-mono text-sm leading-relaxed text-muted2">{c.story}</p>
                <div className="mt-5 flex gap-8">
                  {c.stats.map(([n, l]) => (
                    <div key={l}>
                      <p className="font-bebas text-3xl leading-none text-ink">{n}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted2">{l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
